import React, { useId, useLayoutEffect, useRef, useState } from 'react';

type TextControl = HTMLInputElement | HTMLTextAreaElement;

export function TemplateFieldInput({ value, onValueChange, tokens, rows, className, placeholder, label }: {
  value: string;
  onValueChange: (value: string) => void;
  tokens: string[];
  rows?: number;
  className?: string;
  placeholder?: string;
  label: string;
}) {
  const listId = useId();
  const control = useRef<TextControl | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const caret = useRef<number | null>(null);
  const dismissed = useRef<string | null>(null);
  const [mention, setMention] = useState<{ start: number; end: number; query: string } | null>(null);
  const [active, setActive] = useState(0);
  const options = [...new Set(tokens)].filter(token => token.toLowerCase().includes(mention?.query.toLowerCase() || ''));
  const selected = Math.min(active, Math.max(0, options.length - 1));

  const updateMention = (input: TextControl) => {
    const end = input.selectionStart ?? input.value.length;
    if (dismissed.current === `${input.value}:${end}`) return;
    dismissed.current = null;
    // A boundary keeps literal email addresses such as name@example.com intact.
    const match = input.value.slice(0, end).match(/(?:^|[\s(,;>])@([\w.-]*)$/);
    setMention(match && input.selectionEnd === end
      ? { start: end - match[1].length - 1, end, query: match[1] } : null);
    setActive(0);
  };

  useLayoutEffect(() => {
    if (caret.current === null) return;
    control.current?.focus();
    control.current?.setSelectionRange(caret.current, caret.current);
    caret.current = null;
  }, [value]);

  useLayoutEffect(() => {
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView?.({ block: 'nearest' });
  }, [selected, mention?.query]);

  const insert = (token: string) => {
    if (!mention) return;
    const replacement = `{{${token}}}`;
    caret.current = mention.start + replacement.length;
    onValueChange(value.slice(0, mention.start) + replacement + value.slice(mention.end));
    setMention(null);
  };

  const props = {
    value, className, placeholder,
    'aria-label': label,
    'aria-autocomplete': 'list' as const,
    'aria-controls': mention ? listId : undefined,
    'aria-expanded': Boolean(mention),
    'aria-activedescendant': mention && options.length ? `${listId}-${selected}` : undefined,
    onChange: (event: React.ChangeEvent<TextControl>) => {
      updateMention(event.currentTarget);
      onValueChange(event.currentTarget.value);
    },
    onSelect: (event: React.SyntheticEvent<TextControl>) => updateMention(event.currentTarget),
    onBlur: () => setMention(null),
    onKeyDown: (event: React.KeyboardEvent<TextControl>) => {
      if (!mention || event.nativeEvent.isComposing) return;
      if (event.key === 'Escape') {
        dismissed.current = `${event.currentTarget.value}:${event.currentTarget.selectionStart}`;
        event.preventDefault(); event.stopPropagation(); setMention(null);
      } else if (options.length && ['ArrowDown', 'ArrowUp', 'Enter', 'Tab'].includes(event.key)) {
        event.preventDefault(); event.stopPropagation();
        if (event.key === 'Enter' || event.key === 'Tab') insert(options[selected]);
        else setActive((selected + (event.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length);
      }
    },
  };

  return (
    <div className="flex min-w-0 flex-col gap-1">
      {rows ? <textarea {...props} rows={rows} ref={element => { control.current = element; }} />
        : <input {...props} type="text" ref={element => { control.current = element; }} />}
      {mention && (
        <div className="rounded-md border border-line bg-elevated shadow-md">
          <div className="border-b border-line px-3 py-2 text-[11px] font-semibold text-ink-3">Insert field</div>
          <div id={listId} ref={list} role="listbox" aria-label="Available fields" className="max-h-44 overflow-y-auto">
            {options.map((token, index) => (
              <button key={token} id={`${listId}-${index}`} type="button" role="option"
                aria-selected={index === selected}
                className={`block w-full break-all px-3 py-2 text-left font-mono text-xs ${index === selected ? 'bg-brand-soft text-brand' : 'text-ink-2 hover:bg-soft'}`}
                onMouseDown={event => event.preventDefault()} onClick={() => insert(token)}>
                {`{{${token}}}`}
              </button>
            ))}
            {!options.length && <p className="px-3 py-2 text-xs text-ink-3">{tokens.length ? 'No matching fields.' : 'Connect a call step with fields to see suggestions.'}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
